import { useCallback, useEffect, useState } from "react";
import { Mic, NotebookPen, History } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

/**
 * Notes timeline rendered below the textarea in the lead drawer.
 * Append-only: every "Save notes" creates a fresh row server-side (lead_notes
 * collection). Voice transcripts surface here too. A legacy entry is appended at
 * the bottom for leads that still carry pre-timeline string notes.
 */
export function NotesTimeline({ leadId, refreshKey }) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    if (!leadId) return;
    setLoading(true);
    setErr(null);
    try {
      const r = await api.get(`/admin/leads/${leadId}/notes`);
      setNotes(r.data?.notes || []);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [leadId]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  if (loading) {
    return (
      <p className="mt-3 text-xs text-slate-500" data-testid="notes-timeline-loading">
        Loading notes…
      </p>
    );
  }
  if (err) {
    return (
      <p className="mt-3 text-xs text-rose-600" data-testid="notes-timeline-error">
        {err}
      </p>
    );
  }
  if (notes.length === 0) {
    return (
      <p className="mt-3 text-xs italic text-slate-400" data-testid="notes-timeline-empty">
        No notes yet. Type above and click Save notes — every entry stays
        timestamped forever.
      </p>
    );
  }

  return (
    <ul
      className="mt-3 space-y-2.5"
      data-testid="notes-timeline"
    >
      {notes.map((n) => {
        const isVoice = n.source === "voice_transcript";
        const isLegacy = n.source === "legacy";
        const Icon = isVoice ? Mic : isLegacy ? History : NotebookPen;
        const iconBg = isVoice
          ? "bg-violet-100 text-violet-700"
          : isLegacy
            ? "bg-amber-100 text-amber-700"
            : "bg-slate-200 text-slate-700";
        return (
          <li
            key={n.id}
            className="rounded-lg border border-slate-200 bg-white p-3 text-sm"
            data-testid={`note-item-${n.id}`}
          >
            <div className="flex items-center gap-2 text-[11px] text-slate-500">
              <span
                className={`inline-flex h-5 w-5 items-center justify-center rounded-md ${iconBg}`}
              >
                <Icon className="h-3 w-3" />
              </span>
              <span className="font-medium text-slate-700">
                {n.created_by_name || "Unknown"}
              </span>
              <span aria-hidden>·</span>
              <span>{fmtRelative(n.created_at)}</span>
              {isVoice && n.duration_ms != null && (
                <span className="ml-1 rounded-full bg-violet-50 px-1.5 py-0.5 text-[10px] font-medium text-violet-800">
                  {Math.round((n.duration_ms || 0) / 1000)}s clip
                </span>
              )}
              {isLegacy && (
                <span className="ml-1 rounded-full bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-800">
                  legacy
                </span>
              )}
            </div>
            <p className="mt-1.5 whitespace-pre-wrap text-slate-800">
              {n.content}
            </p>
          </li>
        );
      })}
    </ul>
  );
}

function fmtRelative(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const diffMs = Date.now() - d.getTime();
  const min = Math.round(diffMs / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (day < 7) return `${day}d ago`;
  return d.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}
